import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Table } from 'antd';
import { InfiniteScroll, SearchInput, Spin } from 'common/components';
import { showSuccessMessage } from 'common/helpers';
import { useUserContext } from 'common/context';
import { DEFAULT_LIST_OFFSET, DEFAULT_PAGINATION_PAGE, LIST_LIMIT_0, LIST_LIMIT_20 } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IAccount } from 'entities/Account/Account.models';
import { ICreateInvitationPayload } from 'entities/Invitation/Invitation.models';
import { SettingsAddUserButton, SettingsUserFilterBtn, SettingsUserFilterChips } from 'entities/Settings/components';
import { renderUserListSettingsRecords } from 'entities/Settings/Settings.helper';
import { UserCard, UserEditCard } from 'entities/User/components';
import { IUserListParams } from 'entities/User/User.models';
import { EUserRole } from 'entities/User/User.enum';
import {
  inviteUser,
  selectInvitationError,
  selectIsInvitationLoading,
  setInvitationError,
} from 'entities/Invitation/Invitation.slice';
import {
  getUserByEmail,
  getUserById,
  getUserContracts,
  getUserList,
  getUserListChunk,
  selectIsUserLoading,
  selectUserCount,
  selectUserList,
  setUserList,
} from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  account: IAccount | null;
  isSeller?: boolean;
}

export const SettingsAccountUserListTab: FC<IComponentProps> = ({ header, account, isSeller }) => {
  const dispatch = useAppDispatch();
  // invitation
  const isInvitationLoading = useAppSelector(selectIsInvitationLoading);
  const invitationError = useAppSelector(selectInvitationError);
  // user
  const userList = useAppSelector(selectUserList);
  const userCount = useAppSelector(selectUserCount);
  const isUserLoading = useAppSelector(selectIsUserLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [params, setParams] = useState<IUserListParams>({
    accountId: account?.id,
    search: '',
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
  });
  const { setOpenUserCard } = useUserContext();

  const onSearch = async (value?: string) => {
    const newParams = {
      ...params,
      search: value,
    };

    setParams(newParams);
    await dispatch(getUserList(newParams));
  };

  const changeFilters = (value: IUserListParams): void => {
    const newParams = { ...params, ...value };
    setParams(newParams);
    dispatch(getUserList(newParams));
  };

  const openCreatedUserCard = async (invitedEmail: string) => {
    const createdUser = await dispatch(getUserByEmail(invitedEmail, account?.id));

    if (createdUser) {
      dispatch(getUserById({ id: createdUser.id, onSuccess: () => setOpenUserCard(true) }));
    }
  };

  const onSendInvite = (payload: ICreateInvitationPayload) => {
    dispatch(setInvitationError(null));
    dispatch(
      inviteUser({
        ...payload,
        onSuccess: () => {
          payload.onSuccess?.();
          showSuccessMessage(`Приглашение успешно отправлено на почту ${payload.email}`);
          dispatch(getUserList(params));
          openCreatedUserCard(payload.email as string);
        },
      }),
    );
  };

  const onResendInvite = (payload: ICreateInvitationPayload) => {
    dispatch(
      inviteUser({
        ...payload,
        onSuccess: () => {
          payload.onSuccess?.();
          showSuccessMessage(`Приглашение успешно отправлено на почту ${payload.email}`);
        },
      }),
    );
  };

  useEffect(() => {
    const fetch = async () => {
      dispatch(setUserList([]));
      await dispatch(getUserList(params));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  return (
    <>
      <InfiniteScroll
        loading={scrollLoading}
        hasMore={!isUserLoading && userList.length < userCount}
        loader={<Spin />}
        scrollToTopTrigger={[params]}
        loadMore={async () => {
          setScrollLoading(true);
          setPage(page + 1);
          await dispatch(
            getUserListChunk({
              ...params,
              offset: LIST_LIMIT_20 * page,
            }),
          );
          setScrollLoading(false);
        }}
      >
        {header}

        <div className="account-user-list-settings__filters">
          <SearchInput auto placeholder="Введите имя, фамилию или e-mail" onSearch={onSearch} />

          <SettingsAddUserButton error={invitationError} loading={isInvitationLoading} onSendInvite={onSendInvite} />

          <SettingsUserFilterBtn isSeller={isSeller} params={params} onChangeFilter={changeFilters} />
        </div>

        <SettingsUserFilterChips params={params} onClearFilter={changeFilters} />

        <Table
          className={`mb-19 table-hover ${!userList.length && 'table-empty'} account-user-list-settings__table`}
          showHeader={false}
          pagination={false}
          size="middle"
          dataSource={userList.map((user) => ({ ...user, key: user.id }))}
          columns={renderUserListSettingsRecords(onResendInvite, isSeller)}
          locale={{
            emptyText: fetchLoading ? <Spin /> : <div className="my-11 text-body color-dark-grey">Список пользователей пуст</div>,
          }}
          loading={isInvitationLoading || isUserLoading}
          onRow={({ id, roles }) => ({
            onClick: async () => {
              if (roles.includes(EUserRole.SellerManager)) {
                await dispatch(getUserContracts({ userId: id, limit: LIST_LIMIT_0 }));
              }

              dispatch(
                getUserById({
                  id,
                  onSuccess: () => {
                    setOpenUserCard(true);
                  },
                }),
              );
            },
          })}
        />
      </InfiniteScroll>

      <UserCard />

      <UserEditCard />
    </>
  );
};
